import { CheckCircle2, Circle, XCircle, MinusCircle } from "lucide-react";
import type { B2BInvoice } from "../types";

type Variant = "done" | "current" | "pending" | "rejected" | "skipped";

interface Step {
  label: string;
  at: string | null;
  by?: string | null;
  variant: Variant;
}

function formatDt(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Wytyczne biznesowe: cykl Złożona → Zatwierdzona → Zaksięgowana → Przekazana → Opłacona,
 * z bocznym przejściem Odrzucona (ze Złożonej), które zamyka cykl po stronie operacyjnej. */
function buildSteps(inv: B2BInvoice): Step[] {
  const isRejected = inv.status === "rejected";

  const steps: Step[] = [];

  steps.push({
    label: "Utworzona",
    at: inv.created_at,
    variant: "done",
  });

  steps.push({
    label: "Złożona",
    at: inv.submitted_at,
    variant: inv.submitted_at
      ? "done"
      : inv.status === "draft"
        ? "current"
        : "pending",
  });

  if (isRejected) {
    steps.push({
      label: "Odrzucona przez CEO",
      at: inv.rejected_at,
      by: inv.approved_by_name,
      variant: "rejected",
    });
    steps.push({ label: "Zatwierdzona", at: null, variant: "skipped" });
    steps.push({ label: "Zaksięgowana", at: null, variant: "skipped" });
    steps.push({ label: "Przekazana do opłacenia", at: null, variant: "skipped" });
    steps.push({ label: "Opłacona", at: null, variant: "skipped" });
    return steps;
  }

  steps.push({
    label: "Zatwierdzona przez CEO",
    at: inv.approved_at,
    by: inv.approved_by_name,
    variant: inv.approved_at
      ? "done"
      : inv.status === "submitted"
        ? "current"
        : "pending",
  });
  steps.push({
    label: "Zaksięgowana",
    at: inv.booked_at,
    by: inv.booked_by_name,
    variant: inv.booked_at
      ? "done"
      : inv.status === "approved"
        ? "current"
        : "pending",
  });
  steps.push({
    label: "Przekazana do opłacenia",
    at: inv.transferred_at,
    by: inv.transferred_by_name,
    variant: inv.transferred_at
      ? "done"
      : inv.status === "booked"
        ? "current"
        : "pending",
  });
  steps.push({
    label: "Opłacona",
    at: inv.paid_at,
    by: inv.paid_by_name,
    variant: inv.paid_at
      ? "done"
      : inv.status === "transferred_for_payment"
        ? "current"
        : "pending",
  });

  return steps;
}

function variantStyles(v: Variant) {
  switch (v) {
    case "done":
      return {
        Icon: CheckCircle2,
        iconClass: "text-emerald-500",
        line: "bg-emerald-300",
        title: "text-slate-900 font-medium",
        sub: "text-slate-500",
      };
    case "current":
      return {
        Icon: Circle,
        iconClass: "text-sky-500 fill-sky-100 animate-pulse",
        line: "bg-slate-200",
        title: "text-sky-700 font-medium",
        sub: "text-sky-600",
      };
    case "rejected":
      return {
        Icon: XCircle,
        iconClass: "text-rose-500",
        line: "bg-rose-200",
        title: "text-rose-700 font-medium",
        sub: "text-rose-600",
      };
    case "skipped":
      return {
        Icon: MinusCircle,
        iconClass: "text-slate-200",
        line: "bg-slate-100",
        title: "text-slate-300 line-through",
        sub: "text-slate-300",
      };
    case "pending":
    default:
      return {
        Icon: Circle,
        iconClass: "text-slate-300",
        line: "bg-slate-200",
        title: "text-slate-500",
        sub: "text-slate-400",
      };
  }
}

export function StatusTimeline({ inv }: { inv: B2BInvoice }) {
  const steps = buildSteps(inv);

  return (
    <ol className="space-y-0">
      {steps.map((step, i) => {
        const isLast = i === steps.length - 1;
        const s = variantStyles(step.variant);
        const { Icon } = s;
        return (
          <li key={step.label} className="flex gap-3">
            <div className="flex flex-col items-center pt-0.5">
              <Icon size={20} className={`flex-shrink-0 ${s.iconClass}`} />
              {!isLast && (
                <div className={`w-0.5 grow my-1 ${s.line}`} style={{ minHeight: 24 }} />
              )}
            </div>
            <div className="flex-1 pb-5 -mt-0.5">
              <div className={`text-sm ${s.title}`}>{step.label}</div>
              {step.at ? (
                <div className={`text-xs mt-0.5 ${s.sub}`}>
                  <time dateTime={step.at}>{formatDt(step.at)}</time>
                  {step.by && (
                    <>
                      {" · "}
                      <span className="font-medium">{step.by}</span>
                    </>
                  )}
                </div>
              ) : step.variant === "current" ? (
                <div className={`text-xs mt-0.5 ${s.sub}`}>Oczekuje na akcję</div>
              ) : step.variant === "pending" ? (
                <div className={`text-xs mt-0.5 ${s.sub}`}>—</div>
              ) : step.variant === "skipped" ? (
                <div className={`text-xs mt-0.5 ${s.sub}`}>krok pominięty</div>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
